import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AppBreadcrumbs from '../../components/AppBreadcrumbs';

/**
 * Formats an ISO 8601 date string as a relative time string.
 */
function formatRelativeTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  if (diffWeek < 5) return `${diffWeek} week${diffWeek === 1 ? '' : 's'} ago`;
  return `${diffMonth} month${diffMonth === 1 ? '' : 's'} ago`;
}

/**
 * PageHeader — Modern Enterprise Design System
 *
 * Clean, minimal page header with proper hierarchy and spacing.
 */
function PageHeader({
  title,
  subtitle,
  icon: Icon,
  iconColor,
  actions,
  breadcrumbs = true,
  currentBreadcrumb,
  statusBadge,
  lastUpdated,
}) {
  const theme = useTheme();
  // The app shell (LayoutShell) renders a BreadcrumbBar on desktop, so PageHeader
  // must NOT render its own breadcrumb there (it would duplicate). On mobile the
  // shell hides its breadcrumb, so PageHeader renders it to keep navigation context.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!title) return null;

  return (
    <Box sx={{ mb: 3 }}>
      {breadcrumbs && isMobile && <AppBreadcrumbs current={currentBreadcrumb} />}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          '@media (max-width: 599px)': {
            flexDirection: 'column',
            gap: 1.5,
          },
        }}
      >
        {/* Left: icon + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
          {Icon && (
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                bgcolor: (theme) => iconColor
                  ? `${iconColor}12`
                  : `${theme.palette.primary.main}12`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon sx={{
                fontSize: 20,
                color: (theme) => iconColor || theme.palette.primary.main,
              }} />
            </Box>
          )}

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.3,
                  letterSpacing: '-0.01em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {title}
              </Typography>
              {statusBadge}
            </Box>

            {subtitle && (
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  mt: 0.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {subtitle}
              </Typography>
            )}

            {lastUpdated && (
              <Typography
                sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 0.25 }}
              >
                Updated {formatRelativeTime(lastUpdated)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right: actions */}
        {actions && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              '@media (max-width: 599px)': { alignSelf: 'flex-start' },
            }}
          >
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default PageHeader;
